import type {
  IRCounter,
  IRCount,
  IRDelta,
  IRExpression,
  IRInstruction,
  IRLink,
  IRParam,
  IRPattern,
  IRSegment,
  IRStep,
} from '../types/ir';

class DslBuilder {
  private lines: string[] = [];
  private indentLevel = 0;

  append(text: string) {
    this.lines.push('  '.repeat(this.indentLevel) + text);
  }

  indent() {
    this.indentLevel++;
  }

  dedent() {
    this.indentLevel--;
  }

  build(): string {
    return this.lines.join('\n');
  }
}

function generateExpression(expr: IRExpression): string {
  switch (expr.kind) {
    case 'literal':
      if (expr.type === 'text') {
        return `"${escapeDslString(String(expr.value))}"`;
      }
      return String(expr.value);
    case 'param_ref':
      return expr.name;
    case 'counter_ref':
      return expr.name;
    case 'unop': {
      const operand = generateExpression(expr.operand);
      if (expr.op === 'neg') return `(-${operand})`;
      return `${expr.op} ${operand}`;
    }
    case 'binop': {
      // This is a simplified precedence handling. A real implementation would be more robust.
      const op = expr.op === 'and' || expr.op === 'or' ? ` ${expr.op} ` : expr.op;
      const left = generateExpression(expr.left);
      const right = generateExpression(expr.right);
      return `(${left} ${op} ${right})`;
    }
  }
}

// Inverse of yy.unquote in src/logic/parser.ts. Escapes \, ", and \n so the
// emitted string round-trips through the parser. Backslash MUST be escaped
// first so the substitutions for " and \n don't get re-escaped.
function escapeDslString(raw: string): string {
  return raw.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
}

function generateMetadata(builder: DslBuilder, metadata: Record<string, string> | undefined) {
  if (!metadata) return;
  const entries = Object.entries(metadata);
  if (entries.length === 0) return;
  for (const [key, value] of entries) {
    builder.append(`metadata ${key} = "${escapeDslString(value)}"`);
  }
  builder.append('');
}

function generateParams(builder: DslBuilder, params: IRParam[]) {
  if (params.length === 0) return;
  for (const param of params) {
    let line = `param ${param.name} : ${param.type} = ${generateExpression(param.default)}`;
    if (param.label !== undefined) {
      line += ` label "${escapeDslString(param.label)}"`;
    }
    builder.append(line);
  }
  builder.append('');
}

function generateLink(link: IRLink): string {
  let s = generateExpression(link.source);
  if (link.when) s += ` when ${generateExpression(link.when)}`;
  s += ` : ${generateExpression(link.delta)}`;
  return s;
}

function generateCounters(builder: DslBuilder, counters: IRCounter[]) {
  if (counters.length === 0) return;
  // The grammar has `counter` not `counters`
  for (const counter of counters) {
    if (counter.kind === 'counter_mutable') {
      let line = `counter ${counter.name} = ${generateExpression(counter.init)}`;
      if (counter.links.length > 0) {
        line += ` links: ${counter.links.map(generateLink).join(', ')}`;
      }
      builder.append(line);
    } else {
      builder.append(`counter ${counter.name} := ${generateExpression(counter.expr)}`);
    }
  }
  builder.append('');
}

function generateDeltas(deltas: IRDelta[]): string {
  if (deltas.length === 0) return '';
  const deltaStrings = deltas.map(
    (d) => `delta ${generateExpression(d.target)}: ${generateExpression(d.expr)}`,
  );
  return ' ' + deltaStrings.join(' ');
}

function generateInstruction(instr: IRInstruction): string {
  if (instr.kind === 'instruction_text') {
    return generateExpression(instr.expr);
  }

  const parts: string[] = [];
  for (const a_case of instr.cases) {
    let caseStr = `when ${generateExpression(a_case.when)}: `;
    caseStr += generateInstruction(a_case.row.instruction);
    caseStr += generateDeltas(a_case.row.deltas);
    parts.push(caseStr);
  }
  if (instr.else) {
    let elseStr = `else: ${generateInstruction(instr.else.instruction)}`;
    elseStr += generateDeltas(instr.else.deltas);
    parts.push(elseStr);
  }
  return `{ ${parts.join(' ')} }`;
}

function generateCount(count: IRCount): string {
  switch (count.kind) {
    case 'count_fixed':
      return generateExpression(count.expr);
    case 'count_until_pred':
      return `until ${generateExpression(count.predicate)}`;
    case 'count_until_text':
      // This part of the grammar seems off, this is a guess
      return `until ${generateExpression(count.text_expr)}`;
  }
}

function generateBody(builder: DslBuilder, body: IRStep[]) {
  builder.indent();
  for (const step of body) {
    generateStep(builder, step);
  }
  builder.dedent();
}

function generateStep(builder: DslBuilder, step: IRStep) {
  switch (step.kind) {
    case 'note':
      builder.append(`note ${generateExpression(step.text_expr)}`);
      break;
    case 'row':
      builder.append(`row ${generateInstruction(step.instruction)}${generateDeltas(step.deltas)}`);
      break;
    case 'group': {
      let groupHeader = 'repeat';
      groupHeader += ` ${generateCount(step.count)}`;
      if (step.iter_name) {
        groupHeader += ` as ${step.iter_name}`;
      }
      groupHeader += ' {';
      builder.append(groupHeader);
      generateCounters(builder, step.counters);
      generateBody(builder, step.body);
      builder.append('}');
      break;
    }
  }
}

const DSL_KEYWORDS = new Set([
  'pattern',
  'param',
  'counter',
  'segment',
  'setup',
  'row',
  'note',
  'repeat',
  'until',
  'as',
  'body',
  'links',
  'delta',
  'when',
  'else',
  'is',
  'odd',
  'even',
  'mod',
  'and',
  'or',
  'not',
  'true',
  'false',
  'int',
  'text',
  'bool',
]);

function formatName(name: string): string {
  const isIdent = /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name);
  if (isIdent && !DSL_KEYWORDS.has(name.toLowerCase())) return name;
  return `"${name.replace(/"/g, '\\"')}"`;
}

function generateSegment(builder: DslBuilder, segment: IRSegment) {
  builder.append(`segment ${formatName(segment.name)} {`);
  builder.indent();

  // When `setup:` / `body:` labels are emitted, the grammar requires counter
  // declarations to be inline inside one of those sections — top-level
  // counter decls before the labels do not parse. Emit counters inside body.
  if (segment.setup.length > 0) {
    builder.append('setup:');
    generateBody(builder, segment.setup);
    builder.append('body:');
    generateCounters(builder, segment.counters);
    generateBody(builder, segment.body);
  } else {
    generateCounters(builder, segment.counters);
    generateBody(builder, segment.body);
  }

  builder.dedent();
  builder.append('}');
}

export function generateDsl(pattern: IRPattern): string {
  const builder = new DslBuilder();

  builder.append(`pattern "${pattern.name}" {`);
  builder.indent();

  generateMetadata(builder, pattern.metadata);
  generateParams(builder, pattern.params);
  generateCounters(builder, pattern.counters);

  for (const segment of pattern.segments) {
    generateSegment(builder, segment);
    builder.append('');
  }

  builder.dedent();
  builder.append('}');

  return builder.build().trim();
}
